import { useState, useRef } from 'react'
import { Paperclip, X, Loader2 } from 'lucide-react'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Checkbox } from '../ui/checkbox'
import { RecipientSelector, RecipientType as UIRecipientType } from './RecipientSelector'
import { useUploadNotificationAttachment } from '../../hooks/useNotifications'
import {
  NotificationType,
  NotificationChannel,
  NotificationPriority,
  CreateNotificationDto,
  NotificationAttachment,
  RecipientType as ApiRecipientType,
  getNotificationTypeLabel,
  getNotificationPriorityLabel,
} from '@tco/shared-types'

// Лимит размера вложения должен совпадать с backend (ATTACHMENT_MAX_BYTES)
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024 // 10MB

function formatFileSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

interface NotificationFormProps {
  onSubmit: (data: CreateNotificationDto, sendImmediately: boolean) => void
  isLoading?: boolean
  initialData?: Partial<CreateNotificationDto>
  mode?: 'create' | 'edit'
}

export function NotificationForm({
  onSubmit,
  isLoading = false,
  initialData,
  mode = 'create',
}: NotificationFormProps) {
  // Form state
  const [type, setType] = useState<NotificationType>(
    initialData?.type || NotificationType.GENERAL
  )
  const [title, setTitle] = useState(initialData?.title || '')
  const [body, setBody] = useState(initialData?.body || '')
  const [priority, setPriority] = useState<NotificationPriority>(
    initialData?.priority || NotificationPriority.NORMAL
  )
  const [channels, setChannels] = useState<NotificationChannel[]>(
    initialData?.channels || [NotificationChannel.PUSH]
  )

  // Recipient state (using UI RecipientType)
  const [recipientType, setRecipientType] = useState<UIRecipientType>(() => {
    if (initialData?.is_broadcast) return 'broadcast'
    if (initialData?.recipient_employee_id) return 'employee'
    if (initialData?.recipient_role_id) return 'role'
    if (initialData?.recipient_department_id || initialData?.department_ids?.length)
      return 'department'
    return 'employee'
  })
  const [recipientId, setRecipientId] = useState(() => {
    return (
      initialData?.recipient_employee_id ||
      initialData?.recipient_role_id ||
      ''
    )
  })
  // Отделы-получатели (мультивыбор для типа «Отдел»)
  const [departmentIds, setDepartmentIds] = useState<string[]>(() => {
    if (initialData?.department_ids?.length) return initialData.department_ids
    if (initialData?.recipient_department_id) return [initialData.recipient_department_id]
    return []
  })

  // Scheduling state
  const [scheduledFor, setScheduledFor] = useState(initialData?.scheduled_for || '')
  const [sendImmediately, setSendImmediately] = useState(true)

  // Attachments state
  const [attachments, setAttachments] = useState<NotificationAttachment[]>(
    initialData?.attachments || []
  )
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadMutation = useUploadNotificationAttachment()

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleFilesSelected = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    setUploadError(null)

    const files = Array.from(fileList)
    for (const file of files) {
      if (file.size > ATTACHMENT_MAX_BYTES) {
        setUploadError(`Файл "${file.name}" превышает 10 МБ`)
        continue
      }
      try {
        const uploaded = await uploadMutation.mutateAsync(file)
        setAttachments((prev) => [...prev, uploaded])
      } catch (err) {
        setUploadError(
          err instanceof Error ? err.message : `Не удалось загрузить "${file.name}"`
        )
      }
    }

    // Сбрасываем input, чтобы повторный выбор того же файла сработал
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  // Channel toggle handlers
  const toggleChannel = (channel: NotificationChannel) => {
    setChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((c) => c !== channel)
        : [...prev, channel]
    )
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!title.trim()) {
      newErrors.title = 'Заголовок обязателен'
    }

    if (!body.trim()) {
      newErrors.body = 'Текст уведомления обязателен'
    }

    if (channels.length === 0) {
      newErrors.channels = 'Выберите хотя бы один канал доставки'
    }

    if (recipientType === 'department') {
      if (departmentIds.length === 0) {
        newErrors.recipient = 'Выберите хотя бы один отдел'
      }
    } else if (recipientType !== 'broadcast' && !recipientId) {
      newErrors.recipient = 'Выберите получателя'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    // Map frontend UI recipient types to backend API RecipientType enum
    const mapRecipientType = (frontendType: UIRecipientType): ApiRecipientType => {
      switch (frontendType) {
        case 'broadcast':
          return ApiRecipientType.ALL
        case 'department':
          return ApiRecipientType.GROUP
        default: // 'employee', 'role'
          return ApiRecipientType.SINGLE
      }
    }

    const data: CreateNotificationDto = {
      type,
      title: title.trim(),
      message: body.trim(), // Changed from 'body' to 'message'
      recipient_type: mapRecipientType(recipientType),
    }

    // Add recipient info based on type
    if (recipientType === 'employee' || recipientType === 'role') {
      data.recipient_ids = [recipientId] // Backend expects array
    } else if (recipientType === 'department') {
      data.department_ids = departmentIds
    }
    // For broadcast (ALL), no additional fields needed

    // Add scheduling if needed
    if (!sendImmediately && scheduledFor) {
      data.scheduled_at = scheduledFor // Changed from 'scheduled_for'
    }

    // Attach uploaded files
    if (attachments.length > 0) {
      data.attachments = attachments
    }

    onSubmit(data, sendImmediately)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Notification Type */}
      <div className="space-y-2">
        <Label htmlFor="type">Тип уведомления</Label>
        <Select value={type} onValueChange={(value) => setType(value as NotificationType)}>
          <SelectTrigger id="type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(NotificationType).map((notifType) => (
              <SelectItem key={notifType} value={notifType}>
                {getNotificationTypeLabel(notifType)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">
          Заголовок <span className="text-destructive">*</span>
        </Label>
        <Input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Краткий заголовок уведомления"
          disabled={isLoading}
          className={errors.title ? 'border-destructive' : ''}
        />
        {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
      </div>

      {/* Body */}
      <div className="space-y-2">
        <Label htmlFor="body">
          Текст уведомления <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Полный текст уведомления..."
          rows={4}
          disabled={isLoading}
          className={errors.body ? 'border-destructive' : ''}
        />
        {errors.body && <p className="text-xs text-destructive">{errors.body}</p>}
        <p className="text-xs text-muted-foreground">{body.length} символов</p>
      </div>

      {/* Attachments */}
      <div className="space-y-2">
        <Label>Вложения</Label>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
          disabled={isLoading || uploadMutation.isPending}
        />
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Paperclip className="mr-2 h-4 w-4" />
            )}
            {uploadMutation.isPending ? 'Загрузка...' : 'Прикрепить файл'}
          </Button>
        </div>

        {attachments.length > 0 && (
          <ul className="space-y-1">
            {attachments.map((att, index) => (
              <li
                key={`${att.url}-${index}`}
                className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{att.filename}</span>
                  {att.size ? (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatFileSize(att.size)}
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  disabled={isLoading}
                  className="ml-2 shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label="Удалить вложение"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
        <p className="text-xs text-muted-foreground">
          До 10 МБ на файл. Файл будет доступен получателю по ссылке в уведомлении.
        </p>
      </div>

      {/* Priority */}
      <div className="space-y-2">
        <Label htmlFor="priority">Приоритет</Label>
        <Select
          value={priority}
          onValueChange={(value) => setPriority(value as NotificationPriority)}
        >
          <SelectTrigger id="priority">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(NotificationPriority).map((priorityValue) => (
              <SelectItem key={priorityValue} value={priorityValue}>
                {getNotificationPriorityLabel(priorityValue)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Channels */}
      <div className="space-y-2">
        <Label>
          Каналы доставки <span className="text-destructive">*</span>
        </Label>
        <div className="flex flex-col gap-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="channel-push"
              checked={channels.includes(NotificationChannel.PUSH)}
              onCheckedChange={() => toggleChannel(NotificationChannel.PUSH)}
              disabled={isLoading}
            />
            <Label htmlFor="channel-push" className="font-normal cursor-pointer">
              Push-уведомление
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="channel-email"
              checked={channels.includes(NotificationChannel.EMAIL)}
              onCheckedChange={() => toggleChannel(NotificationChannel.EMAIL)}
              disabled={isLoading}
            />
            <Label htmlFor="channel-email" className="font-normal cursor-pointer">
              Email
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="channel-sms"
              checked={channels.includes(NotificationChannel.SMS)}
              onCheckedChange={() => toggleChannel(NotificationChannel.SMS)}
              disabled={isLoading}
            />
            <Label htmlFor="channel-sms" className="font-normal cursor-pointer">
              SMS
            </Label>
          </div>
        </div>
        {errors.channels && <p className="text-xs text-destructive">{errors.channels}</p>}
      </div>

      {/* Recipient Selector */}
      <RecipientSelector
        recipientType={recipientType}
        recipientId={recipientId}
        departmentIds={departmentIds}
        onRecipientTypeChange={setRecipientType}
        onRecipientIdChange={setRecipientId}
        onDepartmentIdsChange={setDepartmentIds}
        disabled={isLoading}
      />
      {errors.recipient && <p className="text-xs text-destructive">{errors.recipient}</p>}

      {/* Scheduling */}
      {mode === 'create' && (
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="send-immediately"
              checked={sendImmediately}
              onCheckedChange={(checked) => setSendImmediately(checked === true)}
              disabled={isLoading}
            />
            <Label htmlFor="send-immediately" className="font-normal cursor-pointer">
              Отправить немедленно
            </Label>
          </div>

          {!sendImmediately && (
            <div className="space-y-2 pl-6">
              <Label htmlFor="scheduled-for">Запланировать отправку на</Label>
              <Input
                id="scheduled-for"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Уведомление будет отправлено в указанное время
              </p>
            </div>
          )}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading ? 'Обработка...' : mode === 'create' ? 'Создать уведомление' : 'Сохранить изменения'}
        </Button>
      </div>
    </form>
  )
}
